// src/services/api.js
//
// Axios instance + a tiny export that other modules use when they need
// to construct absolute URLs to assets served from the same origin as
// the API (e.g. /ftp/<customerId>/logos/<filename>). Those go directly
// through <img src>, not via axios, so they need the full host.

import axios from 'axios';
import router from '../router';

// Raw env value: typically 'https://kdev.datafangst.dk/api' in dev or
// 'http://127.0.0.1/api' for local. The trailing /api is the axios
// baseURL — strip it off to get the bare origin for static assets.
const API_BASE = process.env.VUE_APP_API_URL || 'http://127.0.0.1/api';

/**
 * Origin (scheme + host + optional port) of the API server, with no
 * trailing path. Use this when you need to point an <img>, <a>, or
 * download URL at a static file served from the API host's public/
 * directory.
 *
 * Example:
 *   API_ORIGIN + logo.url
 *   → 'https://kdev.datafangst.dk' + '/ftp/1/logos/abc.png'
 *
 * Without this, a relative path like '/ftp/1/logos/abc.png' resolves
 * against the Vue dev server's origin (192.168.0.153:8080) instead of
 * the API's, which 404s because the Vue dev server doesn't host those
 * files.
 */
export const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const apiClient = axios.create({
  baseURL: API_BASE,
});

// Request interceptor — attach the user token to every outgoing call.
apiClient.interceptors.request.use(
  (config) => {
    const userToken = localStorage.getItem('userToken');
    if (userToken) {
      config.headers['Authorization'] = `Bearer ${userToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — surface 401s as a redirect to the login page
// after wiping the now-invalid token. Anything else passes through to
// the caller's catch block.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('userToken');
      router.push({ name: 'login' });
    }
    return Promise.reject(error);
  }
);

export default apiClient;